'use client'

import { useTranslations } from 'next-intl'
import { LocaleLink } from '@/components/LocaleLink'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

import type { HomeSectionNavItem } from './types'

type HomeSectionCategoryNavProps = {
  items: HomeSectionNavItem[]
  className?: string
  /** Rótulo acessível da navegação. */
  ariaLabel?: string
}

export function HomeSectionCategoryNav({
  items,
  className,
  ariaLabel,
}: HomeSectionCategoryNavProps) {
  const t = useTranslations('Home')
  const resolvedAriaLabel = ariaLabel ?? t('navByCategory')
  const [activeId, setActiveId] = useState(items[0]?.id ?? '')
  const scrollRef = useRef<HTMLUListElement>(null)
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false })

  const updateScrollEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    setScrollEdges({
      left: el.scrollLeft > 2,
      right: maxScroll > 2 && el.scrollLeft < maxScroll - 2,
    })
  }, [])

  useLayoutEffect(() => {
    updateScrollEdges()
  }, [updateScrollEdges, items])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const observer = new ResizeObserver(updateScrollEdges)
    observer.observe(el)
    return () => observer.disconnect()
  }, [updateScrollEdges, items])

  if (items.length === 0) return null

  return (
    <nav
      aria-label={resolvedAriaLabel}
      data-home-region="news-nav"
      className={cn('relative', className)}
    >
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background via-background/95 to-transparent transition-opacity duration-200 max-lg:block lg:hidden',
          scrollEdges.left ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background via-background/95 to-transparent transition-opacity duration-200 max-lg:block lg:hidden',
          scrollEdges.right ? 'opacity-100' : 'opacity-0',
        )}
      />

      <ul
        ref={scrollRef}
        onScroll={updateScrollEdges}
        className="flex touch-pan-x gap-2 overflow-x-auto scroll-smooth pb-0.5 max-lg:snap-x max-lg:snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:overflow-visible"
      >
        {items.map((item) => {
          const isActive = item.id === activeId
          const buttonClass =
            'h-8 shrink-0 snap-start rounded-full px-3.5 text-xs font-semibold tracking-wide'

          return (
            <li key={item.id} className="shrink-0 snap-start">
              {item.href ? (
                <Button
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className={buttonClass}
                  asChild
                >
                  <LocaleLink
                    href={item.href}
                    onClick={() => setActiveId(item.id)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.label}
                  </LocaleLink>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className={buttonClass}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => setActiveId(item.id)}
                >
                  {item.label}
                </Button>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
